/**
 * Applies a user soft-delete or restore and reports the outcome to the
 * person who asked for it. One hook behind both the settings Danger Zone and
 * the user grid's selection bar, so a delete from either place toasts the
 * same way and reads the same result buckets.
 *
 * Contains:
 * - useUserLifecycleAction — { run(action, target), submitting }
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useApplyUserLifecycleMutation } from '@sophocles/features/entities/users';
import { addToast } from '@sophocles/features/toast';
import { parseApiError } from '@sophocles/utils/apiErrors';

import { lifecycleBlockerMessage, lifecycleSuccessMessage } from './lifecycleMessages';
import { summarizeOutcome } from './userLifecycle';

/**
 * A lifecycle target is either explicit accounts, `{ userIds }`, or the
 * grid's whole filtered set, `{ selectAll: true, filters, count }`, where
 * `filters` are the grid's JSON:API params and `count` the server total.
 *
 * @typedef {{ userIds?: number[], selectAll?: boolean, filters?: object, count?: number }} LifecycleTarget
 */

/**
 * @returns {{
 *   run: (action: 'delete'|'restore', target: LifecycleTarget) => Promise<ReturnType<typeof summarizeOutcome> | null>,
 *   submitting: boolean,
 * }} `run` resolves to the summarized outcome, or null when the request
 *   itself failed (already toasted). Callers refresh their own surface when
 *   `outcome.applied` is true.
 */
export default function useUserLifecycleAction() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [applyLifecycle, { isLoading: submitting }] = useApplyUserLifecycleMutation();

  const notify = useCallback(
    (level, message) =>
      dispatch(
        addToast({
          title: level === 'success' ? t('general.success') : t('general.error'),
          message,
          level,
          dismissible: true,
        }),
      ),
    [dispatch, t],
  );

  const run = useCallback(
    async (action, target) => {
      const { userIds = [], selectAll = false, filters, count = 0 } = target;
      const requestedCount = selectAll ? count : userIds.length;
      let body;
      try {
        body = await applyLifecycle({ action, userIds, selectAll, filters }).unwrap();
      } catch (error) {
        const { nonFieldMessages } = parseApiError(error, {
          knownFields: [],
          genericMessage: t('errors.unexpected', 'Something went wrong. Please try again.'),
        });
        notify('error', nonFieldMessages.join(' '));
        return null;
      }
      const outcome = summarizeOutcome(action, body);
      if (!outcome.applied) {
        const blocker = outcome.blockers[0];
        notify(
          'error',
          lifecycleBlockerMessage(t, blocker?.code, {
            count: blocker?.ids.length || 1,
            isPlural: requestedCount > 1,
          }),
        );
        return outcome;
      }
      notify('success', lifecycleSuccessMessage(t, action, outcome.appliedIds.length));
      return outcome;
    },
    [applyLifecycle, notify, t],
  );

  return { run, submitting };
}
